import { useEffect, useState } from "react";

import Box from "@/components/Box";
import FilterIcon from "@/images/FilterIcon";
import Checkbox from "@/tpo/Checkbox";
import Group from "@/tpo/Group";
import Menu from "@/tpo/Menu";
import Stack from "@/tpo/Stack";
import { ActionIcon } from "@/v2/Buttons";
import ButtonV2 from "@/v2/Buttons";
import styled from "styled-components";
import { useTheme } from "styled-components";

import { dashboardSidebarDesktopMinWidth } from "./dashboardLayout";

const TopicFiltersDesktopStack = styled(Stack)`
  display: none;

  @media screen and (min-width: ${dashboardSidebarDesktopMinWidth}) {
    display: flex;
  }
`;

/** sx @media keys are not applied when spread onto Box; use styled-components for hide/show. */
const TopicFiltersMobileWrap = styled(Box)`
  display: flex;

  @media screen and (min-width: ${dashboardSidebarDesktopMinWidth}) {
    display: none;
  }
`;

function TopicFiltersStack({ filters, setFilters, topics, minWidth, maxWidth }) {
  return (
    <>
      <Group justifyContent="space-between">
        <Box fontFamily="gilroyBold" fontSize={32}>
          Filter
        </Box>
        <ButtonV2
          variant="link"
          textTransform="unset"
          textDecoration="underline"
          letterSpacing={0}
          sx={{
            fontSize: 16,
            fontFamily: "gilroyRegular",
            p: 0
          }}
          onClick={() => {
            setFilters(new Set([]));
          }}
        >
          Reset
        </ButtonV2>
      </Group>
      <Stack gap={16}>
        <Box
          fontFamily="gilroyBold"
          color="midGrey"
          letterSpacing={2.8}
          fontSize={12}
          textTransform="uppercase"
          mb={2}
        >
          Topics
        </Box>
        {topics?.map(topic => (
          <Checkbox
            key={topic.id}
            label={topic.name}
            styles={{
              checkbox: {
                size: "sm"
              }
            }}
            checked={filters.has(topic.id)}
            onChange={e => {
              const checked = e.target.checked;
              if (checked) {
                setFilters(new Set([...filters, topic.id]));
              } else {
                setFilters(() => {
                  const newFilters = new Set(filters);
                  newFilters.delete(topic.id);
                  return newFilters;
                });
              }
            }}
          />
        ))}
      </Stack>
    </>
  );
}

export function TopicFiltersMobile({
  filters,
  setFilters,
  topics,
  minWidth,
  maxWidth,
  sx,
  ...props
}) {
  const [listMenuOpen, setListMenuOpen] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    const desktopQuery = window.matchMedia(`(min-width: ${dashboardSidebarDesktopMinWidth})`);
    const closeOnDesktop = () => {
      if (desktopQuery.matches) {
        setListMenuOpen(false);
      }
    };

    closeOnDesktop();
    desktopQuery.addEventListener("change", closeOnDesktop);
    return () => desktopQuery.removeEventListener("change", closeOnDesktop);
  }, []);

  return (
    <TopicFiltersMobileWrap {...props} {...sx}>
      <Menu opened={listMenuOpen} onChange={setListMenuOpen} placement="bottom-end">
        <Menu.Target>
          <ActionIcon
            bg={listMenuOpen ? "dark" : "white"}
            variant="circle"
            size="sm"
            display="flex"
          >
            <FilterIcon fill={listMenuOpen ? "white" : theme.colors.dark} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown
          as={Stack}
          mt={2}
          p={20}
          zIndex={2}
          minWidth={minWidth}
          maxWidth={maxWidth}
          mx="auto"
          borderRadius={10}
          pb={60}
        >
          <TopicFiltersStack filters={filters} setFilters={setFilters} topics={topics} />
        </Menu.Dropdown>
      </Menu>
    </TopicFiltersMobileWrap>
  );
}

export function TopicFiltersDesktop({ filters, setFilters, topics, minWidth, maxWidth, ...props }) {
  return (
    <TopicFiltersDesktopStack
      gap={40}
      border="1px solid black"
      p={20}
      pb={60}
      borderRadius={20}
      minWidth={minWidth}
      maxWidth={maxWidth}
      mb="auto"
      {...props}
    >
      <TopicFiltersStack
        filters={filters}
        setFilters={setFilters}
        topics={topics}
        minWidth={minWidth}
        maxWidth={maxWidth}
      />
    </TopicFiltersDesktopStack>
  );
}
